"use client";

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useId,
    useRef,
    useState,
    type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { X } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { cva, type VariantProps } from "class-variance-authority";
import Heading from "~/componentsDS/Typography/Heading";
import { cn } from "~/lib/utils";

const panelVariants = cva(
    "fixed top-0 bottom-0 z-[110] w-full bg-pureWhite flex flex-col",
    {
        variants: {
            side: {
                right: "right-0 md:rounded-l-2xl",
                left: "left-0 md:rounded-r-2xl",
            },
            size: {
                sm: "md:w-[440px]",
                md: "md:w-[630px]",
                lg: "md:w-[820px]",
                full: "",
            },
        },
        defaultVariants: { side: "right", size: "md" },
    }
);

type DrawerContextValue = {
    titleId: string;
    close: () => void;
};

const DrawerContext = createContext<DrawerContextValue | null>(null);

export const useDrawerContext = () => {
    const ctx = useContext(DrawerContext);
    if (!ctx) {
        throw new Error("useDrawerContext must be used inside <Drawer>");
    }
    return ctx;
};

let scrollLockCount = 0;

const lockBodyScroll = () => {
    if (typeof document === "undefined") return;
    scrollLockCount += 1;
    if (scrollLockCount === 1) {
        document.body.style.overflow = "hidden";
    }
};

const unlockBodyScroll = () => {
    if (typeof document === "undefined") return;
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) {
        document.body.style.overflow = "";
    }
};

type OpenDrawerEntry = { onEscape: () => void };

// Ordered oldest-to-newest so the last entry is the drawer stacked on top.
const openDrawers: OpenDrawerEntry[] = [];

const handleDocumentKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;
    openDrawers[openDrawers.length - 1]?.onEscape();
};

const registerOpenDrawer = (entry: OpenDrawerEntry) => {
    if (openDrawers.length === 0) {
        document.addEventListener("keydown", handleDocumentKeyDown);
    }
    openDrawers.push(entry);
};

const unregisterOpenDrawer = (entry: OpenDrawerEntry) => {
    const index = openDrawers.lastIndexOf(entry);
    if (index !== -1) openDrawers.splice(index, 1);
    if (openDrawers.length === 0) {
        document.removeEventListener("keydown", handleDocumentKeyDown);
    }
};

const FOCUSABLE_SELECTOR =
    "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

export type DrawerSize = NonNullable<VariantProps<typeof panelVariants>["size"]>;
export type DrawerSide = NonNullable<VariantProps<typeof panelVariants>["side"]>;

export interface DrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title?: ReactNode;
    header?: ReactNode;
    footer?: ReactNode;
    children?: ReactNode;
    side?: DrawerSide;
    size?: DrawerSize;
    closeOnOverlayClick?: boolean;
    closeOnEsc?: boolean;
    showCloseButton?: boolean;
    panelClassName?: string;
    overlayClassName?: string;
    headerClassName?: string;
    bodyClassName?: string;
    footerClassName?: string;
}

const Drawer = ({
    open,
    onOpenChange,
    title,
    header,
    footer,
    children,
    side = "right",
    size = "md",
    closeOnOverlayClick = true,
    closeOnEsc = true,
    showCloseButton = true,
    panelClassName,
    overlayClassName,
    headerClassName,
    bodyClassName,
    footerClassName,
}: DrawerProps) => {
    const titleId = useId();
    const panelRef = useRef<HTMLDivElement>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);
    const entryRef = useRef<OpenDrawerEntry>({ onEscape: () => {} });
    const [mounted, setMounted] = useState(false);

    const close = useCallback(() => onOpenChange(false), [onOpenChange]);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (!open) return;
        lockBodyScroll();
        return () => unlockBodyScroll();
    }, [open]);

    useEffect(() => {
        entryRef.current.onEscape = () => {
            if (closeOnEsc) close();
        };
    }, [closeOnEsc, close]);

    // Registration is keyed on `open` alone so prop changes never reshuffle the
    // stacking order the Escape handler relies on.
    useEffect(() => {
        if (!open) return;
        const entry = entryRef.current;
        registerOpenDrawer(entry);
        return () => unregisterOpenDrawer(entry);
    }, [open]);

    useEffect(() => {
        if (!open) return;

        previousFocusRef.current = (document.activeElement as HTMLElement) ?? null;

        const focusTimer = window.setTimeout(() => {
            const node = panelRef.current;
            if (!node) return;
            const firstFocusable = node.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
            (firstFocusable ?? node).focus();
        }, 0);

        return () => {
            window.clearTimeout(focusTimer);
            previousFocusRef.current?.focus?.();
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const handleTab = (event: KeyboardEvent) => {
            if (event.key !== "Tab") return;
            const node = panelRef.current;
            if (!node) return;

            const focusables = Array.from(
                node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
            );

            if (focusables.length === 0) {
                event.preventDefault();
                node.focus();
                return;
            }

            const first = focusables[0]!;
            const last = focusables[focusables.length - 1]!;
            const active = document.activeElement;

            if (event.shiftKey && active === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", handleTab);
        return () => document.removeEventListener("keydown", handleTab);
    }, [open]);

    if (!mounted) return null;

    const initialX = side === "right" ? "100%" : "-100%";

    const renderHeader = () => {
        if (header) return header;

        return (
            <div
                className={cn(
                    "flex items-center justify-between gap-4 px-6 md:px-8 pt-8 pb-6 md:pb-10",
                    headerClassName
                )}
            >
                {title ? (
                    <Heading
                        as="h2"
                        size="sm"
                        sizes={{ md: "md" }}
                        className="text-primary500 pt-[2px]"
                    >
                        <span id={titleId}>{title}</span>
                    </Heading>
                ) : (
                    <span id={titleId} className="sr-only">
                        Drawer
                    </span>
                )}
                {showCloseButton && (
                    <button
                        type="button"
                        onClick={close}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors ml-auto shrink-0"
                        aria-label="Close drawer"
                    >
                        <X size={32} className="text-trueBlack" />
                    </button>
                )}
            </div>
        );
    };

    return createPortal(
        <DrawerContext.Provider value={{ titleId, close }}>
            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            className={cn(
                                "fixed inset-0 bg-black/50 z-[100]",
                                overlayClassName
                            )}
                            onClick={() => {
                                if (closeOnOverlayClick) close();
                            }}
                            aria-hidden="true"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2, ease: "linear" }}
                        />

                        <motion.div
                            ref={panelRef}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby={titleId}
                            tabIndex={-1}
                            className={cn(panelVariants({ side, size }), panelClassName)}
                            initial={{ x: initialX }}
                            animate={{ x: 0 }}
                            exit={{ x: initialX }}
                            transition={{ duration: 0.2, ease: "linear" }}
                        >
                            {renderHeader()}

                            <div
                                className={cn(
                                    "flex-1 overflow-y-auto px-6 md:px-8 pb-8",
                                    bodyClassName
                                )}
                            >
                                {children}
                            </div>

                            {footer ? (
                                <div
                                    className={cn(
                                        "px-6 md:px-8 py-6 border-t border-gray200",
                                        footerClassName
                                    )}
                                >
                                    {footer}
                                </div>
                            ) : null}
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </DrawerContext.Provider>,
        document.body
    );
};

export default Drawer;
